import {sortBy} from 'lodash';
import {ServiceBase} from "../ServiceBase";
import {IUserMenuService} from "./user-menu.service.interface";
import {
  IDrawerBoxConfiguration,
  IUsefulLinkConfiguration
} from "../configurations/config-data-sections/drawer.configuration";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {action, observable} from "mobx";
import UsefulLinkActions from "./useful-link-actions";
import {Check} from "@sparkware/app-utils";
import {IUsefulLink} from "./useful-links/useful-link.interface";
import {ConfigUsefulLink} from "./useful-links/config-useful-link";
import {SdkUsefulLink} from "./useful-links/sdk-useful-link";
import {IUsefulLinkSdkData} from "./useful-links/useful-link-data.interface";
import {CHANNEL_ELEMENTS, CHANNEL_SOURCE} from "../../../common/const/casinofied-analytics-consts";
import {AreaType} from "@sparkware/uc-sdk-core/lib/apps/app-enums";
import {IOverlayConsumer} from "../overlay/overlay-consumer.interface";

const ANALYTICS_CATEGORY = 'My_Area';
const USEFUL_LINKS_ACTION = 'Useful Links';

// Logout button is not implemented by SdkUsefulLink; we can filter it out by this ID:
const LOGOUT_BUTTON_ID = 'logout_button';

export class UserMenuService extends ServiceBase implements IUserMenuService, IOverlayConsumer {
  private linkActions: UsefulLinkActions;
  /*
    The user menu is sort of modal:
      - you can open the base user menu (openMenu())
      - you can open the rewards panel (openRewards()), which replaces the
        user menu contents (but is still the same DOM element)

     You can check what's currently open by the properties:
      - isOpen: either the base user menu or a secondary panel is open
      - isRewardsInfo: the rewards secondary panel is open

     When closing things, you can either close just the secondary panel or close
     the whole menu (closeMenu()), which will also close the secondary.
   */

  @observable.ref isOpen: boolean = false;
  @observable.ref isRewardsInfo: boolean = false;

  @observable.ref usefulLinks: IUsefulLink[] = [];

  constructor(serviceFactory: IServiceFactoryExtended) {
    super(serviceFactory);
    this.services.overlay.registerConsumer(this);
    this.linkActions = new UsefulLinkActions(serviceFactory);

    this.reaction(() => this.services.user.isAuthorized,
      () => {
        this.reFetchLinks();
      }, {
        fireImmediately: true,
    });

    this.services.sdk.events.onGameOpen(() => this.services.userMenu.closeMenu());

    this.services.sdk.events.onTouchIDActiveStateChanged(() => this.reFetchLinks())
  }

  private reFetchLinks(){
    this._fetchLinks().then(links => {
      this._setUsefulLinks(links)
    });
  }

  get needsOverlay(): boolean {
    return this.isOpen;
  }

  getOverlayZIndex(): number | undefined {
    return this.zIndex - 1;
  }

  get zIndex(): number {
    return this.services.sideMenu.zIndex - 1;
  }

  onOverlayClicked(): void {
    this.closeMenu();
  }

  @action.bound
  private _setIsOpen(shouldOpen: boolean) {
    this.isOpen = shouldOpen;
  }

  @action.bound
  private _setIsRewardsInfo(shouldBeRewards: boolean) {
    this.isRewardsInfo = shouldBeRewards;
  }

  @action.bound
  private _setUsefulLinks(newLinks: IUsefulLink[]) {
    this.usefulLinks = newLinks;
  }

  private async _fetchLinks() {
    const fetchSteps = [this._fetchConfigLinks(), this._fetchSdkLinks()];
    const stepLinks = await Promise.all(fetchSteps);

    const combinedLinks = stepLinks.reduce((acc, n) => acc.concat(n), []);

    return this.orderLinksByPosition(combinedLinks);
  }

  private orderLinksByPosition(links: IUsefulLink[]): IUsefulLink[]{
    const sortingOrder = {
      "top": 0,
      "bottom": 1
    }

    return sortBy(links, (link) => sortingOrder[link.linkConfig.position] || 0);
  }

  private async _fetchConfigLinks(): Promise<IUsefulLink[]> {
    const configData = this.services.configuration.data.drawer.usefulLinks;

    return configData.map(
      l => new ConfigUsefulLink(l, this._doConfigMenuAction.bind(this), this.services));
  }

  private async _fetchSdkLinks(): Promise<IUsefulLink[]> {
    const sdkLinks = await this.services.sdk.getUsefulLinks();
    // NB: Logout button has special (separate) handling, therefore we don't want it from SDK:
    const filteredOutIds = this._getSdkFilteredOutUsefulLinksIds();
    const pickedLinks = sdkLinks.filter(l => filteredOutIds.indexOf(l.id) < 0);

    return pickedLinks.map(
      l => new SdkUsefulLink(this._doSdkMenuAction.bind(this), l));
  }

  private _getSdkFilteredOutUsefulLinksIds():string[] {
    let ids = [LOGOUT_BUTTON_ID];

    return ids;
  }

  private _doConfigMenuAction(link: IUsefulLinkConfiguration) {
    if (link.analytics && link.analytics.label) {
      this.services.analytics.sendAnalytics(ANALYTICS_CATEGORY, USEFUL_LINKS_ACTION, link.analytics.label);
    }
    this.linkActions.executeAction(link);
    this.closeMenu();
  }

  private _doSdkMenuAction({id, translation}: IUsefulLinkSdkData) {
    this.services.sdk.menuActionExecute(id);
    this.services.analytics.sendAnalytics(ANALYTICS_CATEGORY, USEFUL_LINKS_ACTION, translation);
    this.closeMenu();
  }

  doLogout() {
    this.services.analytics.sendAnalytics(ANALYTICS_CATEGORY, USEFUL_LINKS_ACTION, 'Logout');
    this.services.user.logout();
    this.closeMenu();
  }

  get greeting() {
    const now = new Date();
    const hour = now.getHours();
    const minute = now.getMinutes();

    const {defaultMessage, conditions} = this.services.configuration.data.drawer.welcomeMessage;
    let result = defaultMessage;
    for (const rule of conditions) {
      const [ruleHour, ruleMinute] = rule.time.split(':').map(parseInt);

      if (hour > ruleHour) {
        result = rule.text;
      } else if (hour === ruleHour) {
        // NB: only if the current hour is strictly equal to the rule hour do we need to check the minutes
        if (minute >= ruleMinute) {
          result = rule.text;
        }
      }
    }

    return result;
  }

  launchMyAccount(): void {
    this.services.analytics.sendAnalytics(ANALYTICS_CATEGORY, 'My Account', 'Open_my_Account');
    this.services.externalLinks.openMyAccount();
    this.closeMenu();
  }

  launchDeposit(): void {
    this.services.analytics.sendAnalytics(ANALYTICS_CATEGORY, 'Deposit', 'Open_deposit');
    this.services.cashier.openDeposit({
      channel: {
        source: CHANNEL_SOURCE.casinoClient,
        area: AreaType.UserArea,
        element: CHANNEL_ELEMENTS.depositButton
      }
    });
    this.closeMenu();
  }

  launchWithdraw(): void {
    this.services.analytics.sendAnalytics(ANALYTICS_CATEGORY, 'Withdraw', 'Open_withdraw');
    this.services.cashier.openWithdraw({
      channel: {
        source: CHANNEL_SOURCE.casinoClient,
        area: AreaType.UserArea,
        element: CHANNEL_ELEMENTS.withdrawButton
      }
    });
    this.closeMenu();
  }

  sendLoyaltyAnalytics(): void {
    const {category, action, label} = this.services.configuration.data.drawer.showLoyalty.analytics;
    this.services.analytics.sendAnalytics(category, action, label);
  }

  launchDrawerLink(config: IDrawerBoxConfiguration): void {
    if (Check.isObject(config.seo)) {
      this.services.analytics.sendAnalytics(config.seo.category, config.seo.action, config.seo.label);
    }

    if (this.services.menuFlow.hasCustomFlow(config)) {
      const flowResult = this.services.menuFlow.executeCustomFlow(config);
      if (!flowResult.continue)
        return;
    }

    const source = config.externalLinkSource || config.link;
    const options = config.options || {};
    this.services.externalLinks.openLink({source, options});
    this.closeMenu();
  }

  openRewards(): void {
    this.openMenu();
    this._setIsRewardsInfo(true);
  }

  closeMenu(): void {
    if (this.isOpen) {
      this._setIsOpen(false);
      this._setIsRewardsInfo(false);
    }
  }

  openMenu(): void {
    if (!this.isOpen) {
      this.services.analytics.sendAnalytics('Navigator', 'Navbar', 'Open_UserMenu');
      this._setIsOpen(true);
      this.services.sideMenu.closeMobileMenu();
      this.services.drawer.right.closeDrawer();
    }
  }
}
